import {apiClient} from "../../shared/api/client";
import {
    OnboardingContractError,
    normalizeApplicationDetail,
    normalizeApplicationList,
    normalizeHistoryPage,
} from "./mappers";
import {i18n} from "../../shared/i18n";


const APPLICATIONS_PATH = "/api/v2/admin/business/onboarding/applications";
const PAGE_SIZE = 20;
const ORDERING = "-submitted_at,-id";

export const onboardingKeys = {
    all: ["business-onboarding"],
    applications: (countryScope, filters, page) => [
        "business-onboarding",
        "applications",
        String(countryScope),
        filters,
        page,
    ],
    application: (countryScope, applicationId) => [
        "business-onboarding",
        "application",
        String(countryScope),
        Number(applicationId),
    ],
    historyRoot: (countryScope, applicationId) => [
        "business-onboarding",
        "history",
        String(countryScope),
        Number(applicationId),
    ],
    history: (countryScope, applicationId, page) => [
        "business-onboarding",
        "history",
        String(countryScope),
        Number(applicationId),
        page,
    ],
};

const cleanFilterValue = (value) => (typeof value === "string" ? value.trim() : value);

const buildListParams = ({countryScope, filters = {}, page}) => {
    const params = {
        country_id: String(countryScope),
        status: cleanFilterValue(filters.status),
        flow_type: cleanFilterValue(filters.flow_type),
        search: cleanFilterValue(filters.search),
        submitted_from: cleanFilterValue(filters.submitted_from),
        submitted_to: cleanFilterValue(filters.submitted_to),
        page,
        page_size: PAGE_SIZE,
        ordering: ORDERING,
    };

    return Object.fromEntries(
        Object.entries(params).filter(
            ([, value]) => value !== "" && value !== null && value !== undefined,
        ),
    );
};

const commandConfig = ({applicationId, countryScope, revision, idempotencyKey, signal}) => ({
    params: {country_id: String(countryScope)},
    headers: {
        "If-Match": `"onboarding-${Number(applicationId)}-v${Number(revision)}"`,
        "Idempotency-Key": idempotencyKey,
    },
    ...(signal ? {signal} : {}),
});

export const listApplications = async ({countryScope, filters, page}) => {
    const response = await apiClient.get(APPLICATIONS_PATH, {
        params: buildListParams({countryScope, filters, page}),
    });
    return normalizeApplicationList(response.data);
};

export const getApplication = async ({applicationId, countryScope}) => {
    const response = await apiClient.get(`${APPLICATIONS_PATH}/${Number(applicationId)}`, {
        params: {country_id: String(countryScope)},
    });
    return normalizeApplicationDetail(response.data);
};

export const getHistory = async ({applicationId, countryScope, page}) => {
    const response = await apiClient.get(`${APPLICATIONS_PATH}/${Number(applicationId)}/history`, {
        params: {country_id: String(countryScope), page},
    });
    return normalizeHistoryPage(response.data);
};

const readHeader = (headers, name) => {
    if (!headers) return null;
    if (typeof headers.get === "function") {
        const value = headers.get(name);
        if (value !== undefined && value !== null) return String(value);
    }
    const matchedKey = Object.keys(headers).find((key) => key.toLowerCase() === name.toLowerCase());
    return matchedKey ? String(headers[matchedKey]) : null;
};

const decodeFilename = (value) => {
    try {
        return decodeURIComponent(value);
    } catch (_error) {
        return value;
    }
};

const filenameFromDisposition = (disposition) => {
    if (!disposition) return null;

    const encodedMatch = disposition.match(/filename\*\s*=\s*([^;]+)/i);
    if (encodedMatch) {
        const encodedValue = encodedMatch[1].trim().replace(/^"|"$/g, "");
        const encodedParts = encodedValue.match(/^[^']*'[^']*'(.*)$/);
        const filename = encodedParts ? encodedParts[1] : encodedValue;
        return decodeFilename(filename);
    }

    const quotedMatch = disposition.match(/filename\s*=\s*"([^"]*)"/i);
    if (quotedMatch) return quotedMatch[1];

    const plainMatch = disposition.match(/filename\s*=\s*([^;]+)/i);
    return plainMatch ? plainMatch[1].trim() : null;
};

const replaceControlCharacters = (value) =>
    Array.from(value, (character) => {
        const code = character.codePointAt(0);
        return code <= 31 || code === 127 ? " " : character;
    }).join("");

const sanitizeFilename = (value, documentId) => {
    const fallback = `onboarding-document-${Number(documentId)}`;
    if (typeof value !== "string") return fallback;

    const pathParts = value.replace(/\\/g, "/").split("/");
    const basename = replaceControlCharacters(pathParts[pathParts.length - 1])
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 180);

    return !basename || basename === "." || basename === ".." ? fallback : basename;
};

const MAPPED_KINDS = new Set([
    "network",
    "validation",
    "unauthorized",
    "forbidden",
    "not_found",
    "conflict",
    "rate_limited",
    "server",
    "contract",
]);

const isMappedError = (error) =>
    Boolean(
        error &&
            MAPPED_KINDS.has(error.kind) &&
            typeof error.message === "string" &&
            typeof error.fieldErrors === "object",
    );

const safeIdentifier = (value) =>
    typeof value === "string" && /^[a-z0-9_.:-]+$/i.test(value) ? value : null;

const safeMessage = (value) => {
    if (typeof value !== "string") return null;
    const normalized = value.trim();
    if (!normalized || normalized.length > 500 || /[<>]/.test(normalized)) return null;
    return normalized;
};

const safeFieldErrors = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.entries(value).reduce((result, [key, messages]) => {
        if (!Array.isArray(messages)) return result;
        const safeMessages = messages.map(safeMessage).filter(Boolean);
        if (safeMessages.length > 0) result[key] = safeMessages;
        return result;
    }, {});
};

const positiveIntegerOrNull = (value) => {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : null;
};

const retryAfterSeconds = (headers) => {
    const value = readHeader(headers, "retry-after");
    if (value === null || !/^\d+$/.test(value.trim())) return null;
    return Number(value);
};

const DEFAULT_MESSAGES = {
    get network() { return i18n.t("sections.onboarding.p43"); },
    get validation() { return i18n.t("sections.onboarding.p44"); },
    get unauthorized() { return i18n.t("sections.onboarding.p45"); },
    get forbidden() { return i18n.t("sections.onboarding.p16"); },
    get not_found() { return i18n.t("sections.onboarding.p46"); },
    get conflict() { return i18n.t("sections.onboarding.p47"); },
    get rate_limited() { return i18n.t("sections.onboarding.p48"); },
    get server() { return i18n.t("sections.onboarding.p49"); },
    get contract() { return i18n.t("sections.onboarding.p50"); },
};

const kindForStatus = (status) => {
    if (status === 400 || status === 422) return "validation";
    if (status === 401) return "unauthorized";
    if (status === 403) return "forbidden";
    if (status === 404) return "not_found";
    if (status === 409) return "conflict";
    if (status === 429) return "rate_limited";
    return "server";
};

export const mapOnboardingError = (error) => {
    if (isMappedError(error)) return error;

    if (error instanceof OnboardingContractError) {
        return {
            kind: "contract",
            code: null,
            message: DEFAULT_MESSAGES.contract,
            fieldErrors: {},
            requestId: error.requestId || null,
            retryable: false,
            retryAfterSeconds: null,
            currentRevision: null,
        };
    }

    if (!error?.response) {
        return {
            kind: "network",
            code: null,
            message: DEFAULT_MESSAGES.network,
            fieldErrors: {},
            requestId: null,
            retryable: true,
            retryAfterSeconds: null,
            currentRevision: null,
        };
    }

    const {status, headers} = error.response;
    const data =
        error.response.data &&
        typeof error.response.data === "object" &&
        !Array.isArray(error.response.data)
            ? error.response.data
            : {};
    const kind = kindForStatus(status);
    const canUseResponseMessage =
        kind === "validation" || kind === "conflict" || kind === "rate_limited";
    const mappedRetryAfter = kind === "rate_limited" ? retryAfterSeconds(headers) : null;

    return {
        kind,
        code: safeIdentifier(data.code),
        message:
            (canUseResponseMessage ? safeMessage(data.message) : null) || DEFAULT_MESSAGES[kind],
        fieldErrors: safeFieldErrors(data.field_errors),
        requestId: safeIdentifier(data.request_id),
        retryable: kind === "rate_limited" || Number(status) >= 500,
        retryAfterSeconds: mappedRetryAfter,
        currentRevision: positiveIntegerOrNull(data.current_revision),
    };
};

const mapDownloadError = async (error) => {
    if (isMappedError(error)) return error;
    const response = error?.response;
    if (!response) return mapOnboardingError(error);

    const contentType = readHeader(response.headers, "content-type") || response.data?.type || "";
    let data = response.data;
    if (/application\/(?:[a-z.+-]*\+)?json/i.test(contentType) && typeof data?.text === "function") {
        try {
            data = JSON.parse(await data.text());
        } catch (_error) {
            data = {};
        }
    } else if (typeof Blob !== "undefined" && data instanceof Blob) {
        data = {};
    } else if (/text\/html/i.test(contentType)) {
        data = {};
    }

    return mapOnboardingError({...error, response: {...response, data}});
};

export const downloadDocument = async ({applicationId, documentId, countryScope}) => {
    try {
        const response = await apiClient.get(
            `${APPLICATIONS_PATH}/${Number(applicationId)}/documents/${Number(documentId)}/download`,
            {params: {country_id: String(countryScope)}, responseType: "blob"},
        );
        const disposition = readHeader(response.headers, "content-disposition");
        const filename = sanitizeFilename(filenameFromDisposition(disposition), documentId);
        const contentType =
            readHeader(response.headers, "content-type") ||
            response.data?.type ||
            "application/octet-stream";
        if (
            (typeof Blob !== "undefined" && !(response.data instanceof Blob)) ||
            /(?:application\/(?:[a-z.+-]*\+)?json|text\/html)/i.test(contentType)
        ) {
            throw new OnboardingContractError("document response must be a private file blob");
        }
        return {blob: response.data, filename, contentType};
    } catch (error) {
        throw await mapDownloadError(error);
    }
};

const runCommentCommand = async (path, args) => {
    const response = await apiClient.post(
        `${APPLICATIONS_PATH}/${Number(args.applicationId)}/${path}`,
        {comment: args.comment},
        commandConfig(args),
    );
    return response.data;
};

const runBodylessCommand = async (path, args) => {
    const response = await apiClient.post(
        `${APPLICATIONS_PATH}/${Number(args.applicationId)}/${path}`,
        undefined,
        commandConfig(args),
    );
    return response.data;
};

export const requestDocuments = (args) => runCommentCommand("request-documents", args);

export const rejectApplication = (args) => runCommentCommand("reject", args);

export const publishApplication = (args) => runCommentCommand("publish", args);

export const rotateAccessToken = (args) => runBodylessCommand("access-token/rotate", args);

export const rotateCredentials = (args) => runBodylessCommand("credentials/rotate", args);
